import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SearchableSelect from "@/components/SearchableSelect";
import { toast } from "sonner";

/**
 * Inline "Add Customer" dialog used from the Vehicle form (and anywhere else
 * needing on-the-fly customer creation). City list cascades from the chosen
 * Country — works identically on web and on installed mobile PWA.
 */
export default function AddCustomerDialog({
  open,
  onOpenChange,
  countries,
  onCreated,
}) {
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [cityVal, setCityVal] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setName(""); setCustomerId(""); setPassword("");
      setCountry(""); setCityVal(""); setEmail(""); setPhone("");
      setCities([]);
    }
  }, [open]);

  useEffect(() => {
    if (!country) { setCities([]); return; }
    api.get(`/geo/cities?country_code=${country}`).then((r) => setCities(r.data));
  }, [country]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !country || !cityVal) {
      toast.error("Customer Name, Country and City are required");
      return;
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/customers", {
        name: name.trim(),
        customer_id: customerId.trim(),
        password: password,
        country,
        city: cityVal,
        email: email.trim(),
        phone: phone.trim(),
      });
      toast.success("Customer added");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save customer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border vsm-border max-w-md"
        data-testid="add-customer-dialog"
      >
        <DialogHeader>
          <div className="vsm-overline">Quick Add</div>
          <DialogTitle className="text-2xl font-bold tracking-tight">Add Customer</DialogTitle>
          <DialogDescription className="vsm-muted text-sm">
            Create a new customer without leaving the vehicle form.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="vsm-overline">Customer ID</Label>
            <Input
              data-testid="add-customer-customer-id"
              className="rounded-none mt-2 font-mono"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Optional unique ID"
            />
          </div>
          <div>
            <Label className="vsm-overline">Customer Name <span className="text-red-600">*</span></Label>
            <Input
              data-testid="add-customer-name"
              className="rounded-none mt-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <Label className="vsm-overline">Password</Label>
            <Input
              data-testid="add-customer-password"
              type="password"
              className="rounded-none mt-2 font-mono"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Customer portal password (optional)"
            />
          </div>
          <div>
            <Label className="vsm-overline">Country <span className="text-red-600">*</span></Label>
            <div className="mt-2">
              <SearchableSelect
                testId="add-customer-country"
                value={country}
                onChange={(v) => { setCountry(v); setCityVal(""); }}
                options={countries.map((c) => ({ id: c.code, label: c.name }))}
                placeholder="Select country"
              />
            </div>
          </div>
          <div>
            <Label className="vsm-overline">City <span className="text-red-600">*</span></Label>
            <div className="mt-2">
              <SearchableSelect
                testId="add-customer-city"
                value={cityVal}
                onChange={setCityVal}
                options={cities.map((c) => ({ id: c.name, label: c.name }))}
                placeholder={country ? "Select city" : "Select country first"}
                disabled={!country}
                emptyMessage="No cities for this country"
              />
            </div>
          </div>
          <div>
            <Label className="vsm-overline">Email</Label>
            <Input
              data-testid="add-customer-email"
              type="email"
              className="rounded-none mt-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label className="vsm-overline">Phone</Label>
            <Input
              data-testid="add-customer-phone"
              className="rounded-none mt-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => onOpenChange(false)}
              data-testid="add-customer-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none vsm-primary-bg hover:opacity-90"
              data-testid="add-customer-save"
            >
              {busy ? "Saving…" : "Save Customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
